/* eslint-disable react-refresh/only-export-components */
/**
 * GreenCharge theme system.
 *
 * One tiny module that owns the entire theme surface:
 *   - Storage: localStorage['greencharge-theme'] ∈ {'light','dark','system'}
 *   - Resolution: 'system' consults `prefers-color-scheme`.
 *   - Application: writes data-theme='light|dark' on <html>.
 *   - Provider + hook: <ThemeProvider> / useTheme() for React.
 *   - Live system listener: only attached when preference is 'system';
 *     detached (via effect cleanup) when it isn't, so an explicit
 *     light or dark choice is not clobbered by OS changes.
 *
 * This module is intentionally duplicated in sw-ev-user (identical
 * behaviour, identical storage key + token surface) — sharing across
 * repos via relative imports would be more fragile than keeping two
 * synchronised copies.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

export const THEME_STORAGE_KEY = 'greencharge-theme';
export const VALID_PREFERENCES = new Set(['light', 'dark', 'system']);

/**
 * Read the persisted user preference. Malformed / missing values
 * fall back to 'system' so the OS decides on first load.
 */
export function readStoredPreference() {
  try {
    const raw = typeof window !== 'undefined'
      ? window.localStorage.getItem(THEME_STORAGE_KEY)
      : null;
    if (raw && VALID_PREFERENCES.has(raw)) return raw;
  } catch { /* storage disabled — fall through */ }
  return 'system';
}

function writeStoredPreference(value) {
  try {
    if (value === 'system') {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    }
  } catch { /* storage disabled — no-op */ }
}

/**
 * Resolve the OS-level preference. Returns 'light' when
 * `prefers-color-scheme` matches light, otherwise 'dark' — matching
 * the CSS behaviour that dark is treated as the default when the
 * environment can't state a preference.
 */
export function resolveSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/**
 * Resolve a preference to a concrete 'light' | 'dark' theme.
 */
export function resolveTheme(preference) {
  return preference === 'system' ? resolveSystemTheme() : preference;
}

/**
 * Stamp `data-theme` on <html>. Idempotent — a repeat call with the
 * same value is a no-op, so React re-renders won't cascade DOM churn.
 *
 * On native (Capacitor) we also nudge the platform status-bar to
 * match the theme so the OS chrome above the WebView doesn't stay
 * dark when the user flips to light mode. The call is fire-and-forget
 * — a failure inside the plugin (e.g. simulator quirk) never blocks
 * the DOM update.
 */
export function applyThemeAttribute(resolved) {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  if (el.getAttribute('data-theme') !== resolved) {
    el.setAttribute('data-theme', resolved);
  }
  try {
    // Lazy-load the platform status-bar helper so a pure-web bundle
    // can drop the Capacitor import from its main chunk.
    import('../platform/statusBar.js')
      .then((m) => m.applyTheme?.(resolved))
      .catch(() => {});
  } catch { /* import() rejection is already swallowed above */ }
}

// ── Provider + hook ──────────────────────────────────────────────

// Exported so ThemeToggle can call `useContext(ThemeContext)` directly
// and render nothing when there's no provider in scope (safer than
// throwing inside a component that's mounted from many places).
export const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => readStoredPreference());
  const [resolved, setResolved] = useState(() => resolveTheme(readStoredPreference()));

  // Whenever preference changes, re-resolve and apply. In 'system'
  // mode, also attach a matchMedia listener; the cleanup detaches it
  // when the user picks an explicit theme.
  useEffect(() => {
    const applyNow = () => {
      const next = resolveTheme(preference);
      setResolved(next);
      applyThemeAttribute(next);
    };
    applyNow();

    if (preference !== 'system') return undefined;
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => applyNow();
    // Safari <14 only supports addListener/removeListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (!VALID_PREFERENCES.has(next)) return;
    writeStoredPreference(next);
    setPreferenceState(next);
  }, []);

  const toggle = useCallback(() => {
    // Toggle logic: light → dark → light. If in 'system' mode, snap
    // to the OPPOSITE of the currently-resolved OS theme so the click
    // has a visible effect.
    setPreference(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setPreference]);

  const value = useMemo(() => ({
    preference,      // 'light' | 'dark' | 'system'
    resolved,        // 'light' | 'dark'
    setPreference,
    toggle,
  }), [preference, resolved, setPreference, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// Colocation is deliberate — the hook + provider are the same feature.
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
